// Alert — variant-driven callout. Replaces every inline
// `background: var(--color-warning-soft); padding: ...; border-radius: ...`
// cluster across OnboardingScreen, MATReferralCard, etc.
//
// Variants map to semantic colors. Tone is a soft tint, never a full
// alarm. Danger is reserved for genuinely destructive context — for
// withdrawal-flavored warnings prefer `warning`.

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { Icon } from './Icon';
import { cn } from '../../lib/cn';

type Variant = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps {
  variant?: Variant;
  title?: ReactNode;
  icon?: LucideIcon | null;
  children?: ReactNode;
  className?: string;
}

const DEFAULT_ICON: Record<Variant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

export function Alert({ variant = 'info', title, icon, children, className }: AlertProps) {
  const IconCmp = icon === null ? null : (icon ?? DEFAULT_ICON[variant]);
  return (
    <div className={cn('alert', `alert-${variant}`, className)} role={variant === 'danger' || variant === 'warning' ? 'alert' : undefined}>
      {IconCmp ? (
        <span className="alert-icon" aria-hidden="true">
          <Icon icon={IconCmp} size={18} />
        </span>
      ) : null}
      <div className="alert-body">
        {title ? <p className="alert-title">{title}</p> : null}
        {children ? <div className="alert-content">{children}</div> : null}
      </div>
    </div>
  );
}
